import { redirect } from "react-router";

import type { Route } from "./+types/$locale.catalogue.$locationSlug";
import { getLocationBySlug } from "@/api/locations";
import { buildSeoMeta } from "@/components/seo/meta";
import { Catalog } from "@/features/catalog/routes/Catalog";

/**
 * Build-time loader for /:locale/catalogue/:locationSlug.
 *
 * Resolves the slug → location via getLocationBySlug. On miss, throws a 302
 * redirect to the bare /:locale/catalogue — this replaces the runtime
 * useEffect-navigate-back hack the Catalog component used to carry, so an
 * unknown slug never reaches the component and never gets indexed.
 */
export async function loader({ params }: Route.LoaderArgs) {
    const locale = (params.locale as string) ?? "en";
    const slug = params.locationSlug as string;

    // Prerender sentinel (see react-router.config.ts): resolve without calling
    // the API, and with a 200 — the prerenderer fails the build on ANY non-2xx
    // data response (404s and redirects included). Null renders the bare catalogue.
    if (slug === "__prerender_sentinel__") {
        return { preselectedLocation: null };
    }

    const location = await getLocationBySlug(slug);
    if (!location) {
        throw redirect(`/${locale}/catalogue`);
    }
    return { preselectedLocation: location };
}

export function meta({ loaderData, params, location: routerLocation }: Route.MetaArgs) {
    const loc = loaderData?.preselectedLocation;
    const locale = (params.locale as string) ?? "en";
    const locName = loc?.name ?? "Catalogue";
    const path = routerLocation.pathname.replace(new RegExp(`^/${locale}`), "") || "/catalogue";

    return buildSeoMeta({
        title: loc
            ? `Baby Equipment Rental in ${locName} — Strollers, Cribs & Car Seats`
            : "Baby Equipment Catalogue — Rent or Buy Strollers, Cribs & More",
        description: loc
            ? `Rent premium baby equipment for pickup at ${locName}. Strollers, cribs, car seats and more — clean, safe and ready for your trip or stay.`
            : "Browse our premium baby equipment rental catalogue. Find strollers, cribs, car seats, and more for rent or purchase. Flexible rental periods and affordable pricing.",
        path,
        keywords: loc
            ? `baby equipment rental ${locName}, stroller rental ${locName}, crib rental, car seat rental, baby gear hire`
            : "baby equipment rental, stroller rental, crib rental, car seat rental, baby gear hire, baby equipment for sale",
        // TODO Phase 6: hydrate availableLocales from root loader manifest
        locale,
        availableLocales: [{ code: "en" }],
    });
}

export default function CatalogueByLocationRoute({ loaderData }: Route.ComponentProps) {
    return <Catalog preselectedLocation={loaderData.preselectedLocation ?? undefined} />;
}
